import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getDocumentType, getRFQType } from "actions/RFQ/rfqsAction";
import {
	formValuesGenerator,
	getTabLists,
	validStepChecker,
} from "../../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/createRFQUtils";
import { showToast } from "utils/helpers";
import { RfxContext } from "newui/features/Rfx/context/RfxContext";
import useRefreshWarning from "hooks/useRefreshWarning";
import { logErrorIfDebug } from "utils/logging";
import { mergeCreateRFxFormData } from "../data-processor";
import { touchFieldsRecursively } from "../utils/formValidation";
import { Rfx } from "../../types/rfx.types";

const TAB_NAMES = [
	"Rfx Details",
	"Documentation",
	"Returnable Schedules",
	"Quote",
	"RFx Participants",
];

export interface RfxCreationFormConfig {
	companyId: number | null | undefined;
	forceSaveTabs: number[];
	isTemplate: boolean;
	lastStepTab: number;
	rfxData: Rfx;
	calculateCompletedSteps: (formValues: any, isQuoteValid: boolean) => number[];
	onFormInitialize: (formValues: any) => boolean;
	updateLocalRfxData: (data: any) => void;
}

export const useRfxCreationForm = (config: RfxCreationFormConfig) => {
	const {
		isTemplate,
		lastStepTab,
		forceSaveTabs,
		rfxData,
		updateLocalRfxData,
		companyId,
		calculateCompletedSteps,
		onFormInitialize,
	} = config;

	// Stable refs for callback props to avoid re-render loops in useEffect
	const calculateCompletedStepsRef = useRef(calculateCompletedSteps);
	calculateCompletedStepsRef.current = calculateCompletedSteps;
	const onFormInitializeRef = useRef(onFormInitialize);
	onFormInitializeRef.current = onFormInitialize;

	// ---- State ----
	const [selectedTab, setSelectedTab] = useState(1);
	const formikRef = useRef<any>();
	const [isSubmittingForm, setIsSubmittingForm] = useState(false);
	const [isValidForm, setIsValidForm] = useState(true);
	const [rfxTypes, setRFxTypes] = useState<any[]>([]);
	const [documentCategories, setDocumentCategories] = useState<any[]>([]);
	const [isNotFound, setIsNotFound] = useState(false);
	const [completedSteps, setCompletedSteps] = useState<number[]>([]);
	const [touchedTabs, setTouchedTabs] = useState<number[]>([]);
	const [isFormInitialized, setIsFormInitialized] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [staticOptionsLoaded, setStaticOptionsLoaded] = useState(false);
	const tabRef = useRef<HTMLDivElement | null>(null);

	// ---- Derived ----
	const tabButtonList = getTabLists(isTemplate, rfxData?.rfxType);
	const activeTab = tabButtonList.find((tab: any) => tab.value === selectedTab);
	const lastStep = selectedTab === lastStepTab;
	const enableOnChangeFormik = selectedTab !== 3;

	// ---- Context ----
	const rfxContext = useContext(RfxContext);
	const uploadInProgresses = rfxContext?.uploadInProgresses;
	const isFileUploading = (uploadInProgresses as any)?.rfxProcurer?.length > 0;
	useRefreshWarning(isFileUploading);

	// ---- Effects ----

	// Fetch static options ONCE on mount
	useEffect(() => {
		let isMounted = true;
		setStaticOptionsLoaded(false);
		Promise.all([getRFQType(), getDocumentType()]).then(
			([types, docs]: any[]) => {
				if (!isMounted) {
					return;
				}
				setRFxTypes(types ?? []);
				setDocumentCategories(
					docs?.map((doc: any) => ({ label: doc.name, value: doc.id })) || []
				);
				setStaticOptionsLoaded(true);
			}
		);
		return () => {
			isMounted = false;
		};
	}, []);

	// Validate form and track touched tabs when tab changes
	useEffect(() => {
		formikRef?.current?.validateForm();
		setTouchedTabs((prev) => {
			if (prev.includes(selectedTab)) {
				return prev;
			}
			return [...prev, selectedTab];
		});
	}, [selectedTab]);

	// Touch error fields when submitting
	useEffect(() => {
		if (!isSubmittingForm) {
			return;
		}
		formikRef?.current?.validateForm().then((errors: any) => {
			Object.keys(errors).forEach((fieldName) => {
				formikRef?.current?.setFieldTouched(fieldName, true);
			});
		});
	}, [isSubmittingForm]);

	// Calculate completed steps and initialize form
	useEffect(() => {
		const formValues = formValuesGenerator(rfxData, isTemplate);
		setCompletedSteps(
			calculateCompletedStepsRef.current(formValues, rfxData?.isQuoteTabValid)
		);

		if (rfxData && !isFormInitialized) {
			const didInitialize = onFormInitializeRef.current(formValues);
			if (didInitialize) {
				setIsFormInitialized(true);
			}
		}
	}, [rfxData, companyId, isFormInitialized, isTemplate]);

	// ---- Handlers ----

	const changeTab = useCallback((tab: any) => {
		setSelectedTab(tab.value);
	}, []);

	const onBackButtonClicked = useCallback(() => {
		const currentIndex = tabButtonList.findIndex(
			(item: any) => item.value === selectedTab
		);
		const prevIndex = currentIndex - 1;
		if (prevIndex >= 0) {
			changeTab(tabButtonList[prevIndex]);
		}
		tabRef?.current?.scrollIntoView();
	}, [tabButtonList, selectedTab, changeTab]);

	const resetRfxData = useCallback(
		(rfx: any) => {
			if (!rfx) {
				setIsNotFound(true);
				return;
			}
			updateLocalRfxData(rfx);
			const formValues = formValuesGenerator(rfx, isTemplate);
			const isFormikLoaded = formikRef.current !== undefined;

			const mergedValues = isFormikLoaded
				? mergeCreateRFxFormData(
						formValues,
						selectedTab,
						formikRef.current.values
					)
				: formValues;

			formikRef?.current?.setValues(mergedValues);
		},
		[selectedTab, updateLocalRfxData, isTemplate]
	);

	const checkIsCurrentStepValid = useCallback(
		async (
			handleOnStepValid: any,
			value: any,
			type: any,
			showValidationError = true
		): Promise<boolean | undefined> => {
			const currentForm = formikRef?.current;
			try {
				if (selectedTab === 3) {
					await new Promise((resolve) => setTimeout(resolve, 100));
				}

				const errors = await currentForm?.validateForm();
				const isQuoteTab = selectedTab === 4;

				if (selectedTab === 3) {
					await new Promise<void>((resolve) => {
						touchFieldsRecursively(
							currentForm.values.sections,
							"sections",
							currentForm
						);
						resolve();
					});
				} else if (!isQuoteTab) {
					Object.keys(errors).forEach((fieldName: string) => {
						currentForm.setFieldTouched(fieldName, true);
					});
				}

				const updatedErrors = isQuoteTab
					? null
					: await currentForm.validateForm();

				const validationResult = validStepChecker(
					selectedTab,
					updatedErrors,
					isTemplate
				);

				const isCurrentStepValid =
					validationResult.isValid && touchedTabs.includes(selectedTab);

				if (isCurrentStepValid || isQuoteTab) {
					handleOnStepValid && (await handleOnStepValid(value, type));
				} else if (showValidationError) {
					showToast(validationResult.errorMessage, "Error", false);
				}

				return isCurrentStepValid;
			} catch (error) {
				logErrorIfDebug("Error during validation:", error);
				showToast("An error occurred during validation", "Error", false);
			} finally {
				setIsSubmittingForm(false);
			}
		},
		[selectedTab, touchedTabs, isTemplate]
	);

	/**
	 * Creates a validateOnSave function bound to a specific save handler.
	 * Call this with your page-specific handleSave to get a function that
	 * validates the current step, saves if valid, then calls onSuccess.
	 */
	const makeValidateOnSave = useCallback(
		(onSave: (additionalMessage: any, type: string) => Promise<any>) => {
			return (onSuccess: () => void) => {
				const forceSave = forceSaveTabs.includes(selectedTab);

				checkIsCurrentStepValid(null, selectedTab, null, false).then(
					(isValid) => {
						if (forceSave || isValid) {
							onSave(undefined, "tabchange").then((result: any) => {
								((forceSave && result) || !forceSave) && onSuccess();
							});
						} else {
							const tabName = TAB_NAMES[selectedTab - 1];
							showToast(
								`You must complete all required fields.`,
								`${tabName} Not Saved`,
								false,
								tabName
							);
							onSuccess();
						}
					}
				);
			};
		},
		[forceSaveTabs, selectedTab, checkIsCurrentStepValid]
	);

	/**
	 * Creates a handleTabChangeWithSave function bound to a specific save handler.
	 */
	const makeHandleTabChangeWithSave = useCallback(
		(onSave: (additionalMessage: any, type: string) => Promise<any>) => {
			return (tab: any) => {
				if (isFileUploading) {
					return;
				}
				const validateOnSave = makeValidateOnSave(onSave);
				validateOnSave(() => changeTab(tab));
			};
		},
		[isFileUploading, changeTab, makeValidateOnSave]
	);

	/**
	 * Creates a handleSubmitOutside function bound to a specific save handler and last-step handler.
	 */
	const makeHandleSubmitOutside = useCallback(
		(
			onSave: (additionalMessage: any, type: string) => Promise<any>,
			onLastStep: (type: string, additionalMessage?: string) => void
		) => {
			return async (type: string, additionalMessage?: string) => {
				if (type === "laststep") {
					onLastStep(type, additionalMessage);
				} else {
					const forceSave = forceSaveTabs.includes(selectedTab);
					const isValid = await checkIsCurrentStepValid(
						null,
						null,
						null,
						false
					);
					if (forceSave || isValid) {
						onSave(additionalMessage, type);
					} else {
						const tabName = TAB_NAMES[selectedTab - 1];
						showToast(
							`Complete all required fields`,
							`${tabName} Not Saved`,
							false,
							tabName
						);
					}
				}
			};
		},
		[forceSaveTabs, selectedTab, checkIsCurrentStepValid]
	);

	return {
		// State
		selectedTab,
		formikRef,
		isSubmittingForm,
		setIsSubmittingForm,
		isValidForm,
		setIsValidForm,
		rfxTypes,
		documentCategories,
		isNotFound,
		completedSteps,
		touchedTabs,
		setTouchedTabs,
		openModal,
		setOpenModal,
		isFormInitialized,
		tabRef,
		staticOptionsLoaded,
		isFileUploading,

		// Derived
		tabButtonList,
		activeTab,
		lastStep,
		enableOnChangeFormik,

		// Handlers
		changeTab,
		onBackButtonClicked,
		resetRfxData,
		checkIsCurrentStepValid,

		// Factory functions — page components bind their own save/submit logic
		makeValidateOnSave,
		makeHandleTabChangeWithSave,
		makeHandleSubmitOutside,
	};
};
